import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ModelSelector, Model } from '../AppEditor/ModelSelector';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Send } from 'lucide-react';
import { useApi } from '../../utils/api';
import { useNavigate } from 'react-router-dom';

interface CreateApplicationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onApplicationCreated: () => void;
}

export const CreateApplicationDialog: React.FC<CreateApplicationDialogProps> = ({
  isOpen,
  onClose,
  onApplicationCreated,
}) => {
  const api = useApi();
  const navigate = useNavigate();
  const [appName, setAppName] = useState('');
  const [initialPrompt, setInitialPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('claude-haiku-4-5');
  const [models, setModels] = useState<Model[]>([]);
  const [isCreating, setIsCreating] = useState(false);
  const [pollingMessage, setPollingMessage] = useState('');
  const [packType, setPackType] = useState('bunpack');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // Check if we're in dev mode
  const isDevMode = process.env.REACT_APP_VIBE_ENV === 'dev' || process.env.NODE_ENV === 'development';

  // Fetch available models on mount
  useEffect(() => {
    const fetchModels = async () => {
      try {
        const modelsData = await api.get('/api/models');
        if (modelsData) {
          setModels(modelsData);
        }
      } catch (error) {
        console.error('Failed to fetch models:', error);
        setModels([]);
      }
    };

    if (isOpen) {
      fetchModels();
    }
  }, [isOpen, api]);

  // Auto-resize textarea based on content (matching MessageInput behavior)
  useEffect(() => {
    if (textareaRef.current) {
      // Start with single line height
      textareaRef.current.style.height = '24px';
      // Let it grow based on content
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 100; // ~5 lines (matching MessageInput)
      textareaRef.current.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    }
  }, [initialPrompt]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!initialPrompt.trim()) return;
    
    setIsCreating(true);
    setPollingMessage('Creating application...');

    try {
      // Step 1: Create the application
      const createResponse = await api.post('/api/applications', {
        name: appName || '', // Empty string will trigger auto-generation
        initial_prompt: initialPrompt,
        model: selectedModel,
        ...(isDevMode && { pack_type: packType }), // Only include pack_type in dev mode
      });

      const appId = createResponse.id;
      setPollingMessage('Waiting for editor to be ready...');

      // Step 2: Poll for editor readiness
      const maxAttempts = 60; // 60 seconds max
      let attempts = 0;
      let editorReady = false;

      while (attempts < maxAttempts && !editorReady) {
        try {
          const readyResponse = await api.get(`/api/applications/${appId}/editor-ready`);
          if (readyResponse.ready === 'true') {
            editorReady = true;
            break;
          }
        } catch (error) {
          // Service not ready yet, continue polling
          console.log('Editor not ready yet, retrying...');
        }

        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
        attempts++;
      }

      if (!editorReady) {
        throw new Error('Editor service took too long to become ready');
      }

      // Step 3: Send initial message
      setPollingMessage('Sending initial message...');
      
      await api.post(`/api/demo/applications/${appId}/message`, {
        message: initialPrompt,
        model: selectedModel,
      });

      // Navigate to the editor with the conversation already started
      navigate(`/applications/${appId}/edit`);

      // Notify parent that application was created
      onApplicationCreated();
      
      // Reset form
      setAppName('');
      setInitialPrompt('');
      setPollingMessage('');
      onClose();
    } catch (error) {
      console.error('Failed to create application:', error);
      setPollingMessage('');
      alert('Failed to create application. Please try again.');
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-20 backdrop-blur-sm overflow-y-auto h-full w-full flex items-center justify-center z-50">
      <div className="relative p-8 w-[600px] max-h-[80vh] overflow-y-auto shadow-2xl rounded-2xl bg-white">
        <div className="mt-2">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            Create New Application
          </h3>
          <form onSubmit={handleCreate}>
            {/* App Name */}
            <div className="mb-4">
              <input
                type="text"
                id="appName"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                className="bg-white border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent block w-full p-3 transition-all duration-200"
                placeholder="Application name (optional)"
                disabled={isCreating}
              />
            </div>

            {/* Pack Type Selector (Dev Mode Only) */}
            {isDevMode && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Pack Type
                </label>
                <select
                  value={packType}
                  onChange={(e) => setPackType(e.target.value)}
                  className="bg-white border border-gray-200 text-gray-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent block w-full p-3 transition-all duration-200"
                  disabled={isCreating}
                >
                  <option value="baseapp">Base App (React + Go Backend)</option>
                  <option value="skeleton">Skeleton (HTMX + Templates)</option>
                  <option value="bunpack">Bun Pack (TypeScript + React + Bun)</option>
                </select>
              </div>
            )}

            {/* Message Input styled like MessageInput component */}
            <div className="mb-6">
              <div className="w-full border border-input rounded-md bg-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2" data-message-input-container="true">
                <Textarea
                  ref={textareaRef}
                  value={initialPrompt}
                  onChange={(e) => setInitialPrompt(e.target.value)}
                  placeholder="Describe what you want to build..."
                  disabled={isCreating}
                  required
                  rows={1}
                  className="resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 p-0 mx-3 mt-3 mb-2 overflow-y-auto scrollbar-thin"
                  style={{ 
                    width: 'calc(100% - 24px)', // Account for horizontal margins
                    padding: '0', // Ensure no padding via inline style too
                    minHeight: '24px'
                  }}
                />
                
                {/* Controls at the bottom */}
                <div className="px-2 pb-2 pt-1">
                  {models.length > 0 && (
                    <ModelSelector
                      selectedModel={selectedModel}
                      onModelChange={setSelectedModel}
                      models={models}
                      disabled={isCreating}
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Progress Message */}
            {pollingMessage && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span className="text-sm text-blue-700">{pollingMessage}</span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end gap-3">
              <Button
                type="button"
                onClick={onClose}
                disabled={isCreating}
                variant="outline"
                size="default"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isCreating || !initialPrompt.trim()}
                size="default"
                className="flex items-center gap-2"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Create Application
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};